import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { AttendanceReportFilterBar } from "./utils/AttendanceReportFilterBar";
import type { AttendanceReportRow } from "./types/attendance";
import { fetchAttendanceReport } from "./services/attendanceApi";
import { downloadXlsx } from "./utils/downloadXlsx";
import {
  ALL_FILTER,
  attendanceStudentReportPath,
  humanizePurpose,
  mergeReportRowsBySession,
  reportRowBatchNames,
  rowMatchesFilters,
  uniqueSorted,
} from "./utils/attendanceReportRows";
import {
  AttendanceReportCard,
  AttendanceReportExportButton,
  AttendanceReportSearch,
  ReportSortHeader,
  reportBodyCellStyle,
  reportHeadCellStyle,
} from "./utils/attendanceReportUi";

/** Parse "DD Mon YYYY" (e.g. "31 Jul 2026") for date sorting. */
const parseReportDate = (dateStr: string): number => {
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length === 3) {
    const day = Number(parts[0]);
    const monthIndex = new Date(`${parts[1]} 1, 2000`).getMonth();
    const year = Number(parts[2]);
    if (
      Number.isFinite(day) &&
      Number.isFinite(year) &&
      !Number.isNaN(monthIndex)
    ) {
      return new Date(year, monthIndex, day).getTime();
    }
  }
  const fallback = Date.parse(dateStr);
  return Number.isNaN(fallback) ? 0 : fallback;
};

interface FlatReportRow {
  id: string;
  slNo: number;
  date: string;
  purpose: string;
  course: string;
  batch: string;
  name: string;
  attendanceNo: string;
  totalPresent: string;
  absent: string;
  reportRow: AttendanceReportRow;
}

type SortKey =
  | "slNo"
  | "date"
  | "purpose"
  | "course"
  | "batch"
  | "name"
  | "attendanceNo"
  | "totalPresent"
  | "absent";

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "slNo", label: "Sl No" },
  { key: "date", label: "Date" },
  { key: "purpose", label: "Purpose" },
  { key: "course", label: "Course" },
  { key: "batch", label: "Batch" },
  { key: "name", label: "Name" },
  { key: "attendanceNo", label: "Attendance No" },
  { key: "totalPresent", label: "Total / Present" },
  { key: "absent", label: "Absent" },
];

const AttendanceReport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnToTestPath = (
    location.state as { returnToTestPath?: string } | null
  )?.returnToTestPath;
  const [reportRows, setReportRows] = useState<AttendanceReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(ALL_FILTER);
  const [batch, setBatch] = useState(ALL_FILTER);
  const [purpose, setPurpose] = useState(ALL_FILTER);
  const [sortBy, setSortBy] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [searchQuery, setSearchQuery] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchAttendanceReport({});
      setReportRows(rows);
    } catch (error) {
      console.error("Error fetching attendance report:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const sessionRows = useMemo(
    () => mergeReportRowsBySession(reportRows),
    [reportRows],
  );

  const courses = useMemo(
    () => uniqueSorted(sessionRows.map((row) => row.course)),
    [sessionRows],
  );

  const batchOptions = useMemo(() => {
    const source =
      course === ALL_FILTER
        ? sessionRows
        : sessionRows.filter((row) => row.course === course);
    return uniqueSorted(source.flatMap((row) => reportRowBatchNames(row)));
  }, [sessionRows, course]);

  const purposes = useMemo(
    () => uniqueSorted(sessionRows.map((row) => row.purpose)),
    [sessionRows],
  );

  useEffect(() => {
    if (batch !== ALL_FILTER && !batchOptions.includes(batch)) {
      setBatch(ALL_FILTER);
    }
  }, [batch, batchOptions]);

  const flatRows = useMemo<FlatReportRow[]>(() => {
    const filtered = sessionRows.filter((row) =>
      rowMatchesFilters(row, course, batch, purpose),
    );

    return filtered.flatMap((row) =>
      row.attendanceRounds.map((round) => ({
        id: `${row.attendanceSessionId}-${row.batchId ?? row.batch}-${round.roundNo}`,
        slNo: row.slNo,
        date: row.date,
        purpose: row.purpose,
        course: row.course,
        batch: row.batch,
        name: row.name ?? "",
        attendanceNo: round.label,
        totalPresent: `${round.total} / ${Math.round((round.presentPercent / 100) * round.total)} (${round.presentPercent}%)`,
        absent: `${Math.round((round.absentPercent / 100) * round.total)} (${round.absentPercent}%)`,
        reportRow: row,
      })),
    );
  }, [sessionRows, course, batch, purpose]);

  const searchedRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return flatRows;
    return flatRows.filter((row) =>
      [row.date, row.purpose, row.course, row.batch, row.name, row.attendanceNo]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [flatRows, searchQuery]);

  const sortedRows = useMemo(() => {
    const rows = [...searchedRows];
    rows.sort((a, b) => {
      if (sortBy === "date") {
        const cmp = parseReportDate(a.date) - parseReportDate(b.date);
        return sortDir === "asc" ? cmp : -cmp;
      }
      const av = String(a[sortBy] ?? "");
      const bv = String(b[sortBy] ?? "");
      const cmp = av.localeCompare(bv, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [searchedRows, sortBy, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  const openStudentReport = (row: FlatReportRow) => {
    navigate(attendanceStudentReportPath(row.name || row.reportRow.name || "attendance"), {
      state: {
        reportRow: row.reportRow,
        attendanceNo: row.attendanceNo,
        attendanceSessionId: row.reportRow.attendanceSessionId,
        batchId: row.reportRow.batchId,
        course: row.course,
        batch: row.batch,
        date: row.date,
        purpose: row.purpose,
        ...(returnToTestPath ? { returnToTestPath } : {}),
      },
    });
  };

  const handleDownload = () => {
    const exportRows = sortedRows.map((row, index) => ({
      "Sl No": index + 1,
      Date: row.date,
      Purpose: humanizePurpose(row.purpose),
      Course: row.course,
      Batch: row.batch,
      Name: row.name,
      "Attendance No": row.attendanceNo,
      "Total / Present": row.totalPresent,
      Absent: row.absent,
    }));
    downloadXlsx(exportRows, "Sessions", "attendance_report.xlsx");
  };

  return (
    <AttendanceReportCard
      toolbar={
        <>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <AttendanceReportFilterBar
              courses={courses}
              batches={batchOptions}
              purposes={purposes}
              course={course}
              batch={batch}
              purpose={purpose}
              disabled={loading}
              purposeLabel={humanizePurpose}
              onCourseChange={(value) => {
                setCourse(value);
                setBatch(ALL_FILTER);
              }}
              onBatchChange={setBatch}
              onPurposeChange={setPurpose}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <AttendanceReportSearch
              value={searchQuery}
              onChange={setSearchQuery}
            />
            {returnToTestPath && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(returnToTestPath)}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                  border: "1px solid #ccc",
                  color: "#666",
                  whiteSpace: "nowrap",
                }}
              >
                Return to Test
              </Button>
            )}
            <AttendanceReportExportButton
              onClick={handleDownload}
              disabled={loading || sortedRows.length === 0}
            />
          </Box>
        </>
      }
    >
      <TableContainer
        component={Paper}
        style={{
          marginTop: "8px",
          flexGrow: 1,
          overflow: "auto",
          position: "relative",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.key} style={reportHeadCellStyle()}>
                  <ReportSortHeader
                    label={column.label}
                    active={sortBy === column.key}
                    direction={sortDir}
                    onClick={() => handleSort(column.key)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  style={{ padding: "20px" }}
                >
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  style={{ padding: "20px" }}
                >
                  No attendance records found
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => (
                <TableRow
                  key={row.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => openStudentReport(row)}
                >
                  <TableCell style={reportBodyCellStyle({ textAlign: "center" })}>
                    {index + 1}
                  </TableCell>
                  <TableCell style={reportBodyCellStyle()}>{row.date}</TableCell>
                  <TableCell style={reportBodyCellStyle()}>
                    {humanizePurpose(row.purpose)}
                  </TableCell>
                  <TableCell style={reportBodyCellStyle()}>{row.course}</TableCell>
                  <TableCell style={reportBodyCellStyle()}>{row.batch}</TableCell>
                  <TableCell style={reportBodyCellStyle()}>{row.name}</TableCell>
                  <TableCell style={reportBodyCellStyle({ textAlign: "center" })}>
                    {row.attendanceNo}
                  </TableCell>
                  <TableCell style={reportBodyCellStyle({ textAlign: "center" })}>
                    {row.totalPresent}
                  </TableCell>
                  <TableCell style={reportBodyCellStyle({ textAlign: "center" })}>
                    {row.absent}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </AttendanceReportCard>
  );
};

export default AttendanceReport;
